import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import Logo from '../components/brand/Logo';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const { login, isLoading, error, clearError, isAuthenticated, user } = useAuthStore();

    // Si el usuario ya está autenticado, redirigir a contactos (preventivo)
    useEffect(() => {
        if (isAuthenticated && user) {
            if (user?.role === 'super_admin') {
                navigate('/super-admin/tenants', { replace: true });
            } else {
                navigate('/contacts', { replace: true });
            }
        }
    }, [isAuthenticated, user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearError();

        if (!email || !password) return;

        const success = await login(email, password);
        if (success) {
            // Get user from store state after login
            const user = useAuthStore.getState().user;
            
            if (user?.role === 'super_admin') {
                navigate('/super-admin/tenants', { replace: true });
            } else {
                navigate('/contacts', { replace: true });
            }
        }
    };

    return (
        <div className="flex w-full min-h-screen flex-col lg:flex-row shadow-2xl overflow-hidden">
            {/* Left Side: Login Form */}
            <div className="w-full lg:w-[45%] xl:w-[40%] flex flex-col justify-center items-center bg-white dark:bg-[#1a100c] px-6 py-12 lg:px-12 xl:px-24 relative z-10">

                {/* Logo */}
                <div className="absolute top-8 left-8 lg:left-12 flex items-center gap-2">
                    <div className="size-9 rounded-lg bg-primary flex items-center justify-center text-white">
                        <Logo className="size-7" />
                    </div>
                    <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">TornadoCRM</h1>
                </div>

                <div className="w-full max-w-sm mt-12 lg:mt-0">
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight">Bienvenido de nuevo</h2>
                        <p className="text-slate-500 dark:text-slate-400 text-sm">Ingresa tus credenciales para acceder a tu cuenta.</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm flex items-start gap-3">
                            <span className="material-symbols-outlined text-[20px] shrink-0">error</span>
                            <p>{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email Field */}
                        <div className="space-y-2">
                            <label className="text-slate-700 dark:text-slate-300 text-sm font-medium leading-normal block" htmlFor="email">Correo Electrónico</label>
                            <div className="relative">
                                <input
                                    className="form-input block w-full rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:border-primary focus:ring-primary h-12 px-4 text-base placeholder:text-slate-400"
                                    id="email"
                                    placeholder="ejemplo@empresa.com"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
                                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>mail</span>
                                </div>
                            </div>
                        </div>

                        {/* Password Field */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-slate-700 dark:text-slate-300 text-sm font-medium leading-normal" htmlFor="password">Contraseña</label>
                                <Link to="#" className="text-primary text-sm font-medium hover:underline">¿Olvidaste tu contraseña?</Link>
                            </div>
                            <div className="relative">
                                <input
                                    className="form-input block w-full rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:border-primary focus:ring-primary h-12 px-4 text-base placeholder:text-slate-400 pr-10"
                                    id="password"
                                    placeholder="••••••••"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                                <button
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                                        {showPassword ? 'visibility_off' : 'visibility'}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            className="w-full bg-primary hover:bg-[#b83500] text-white font-semibold rounded-lg h-12 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                            type="submit"
                            disabled={isLoading || !email || !password}
                        >
                            {isLoading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <>
                                    <span>Iniciar Sesión</span>
                                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>arrow_forward</span>
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-slate-500 dark:text-slate-400 text-sm">
                            ¿No tienes cuenta?
                            <Link to="#" className="text-primary font-medium hover:underline ml-1">Regístrate</Link>
                        </p>
                    </div>
                </div>

                {/* Footer Copyright */}
                <div className="absolute bottom-6 w-full text-center">
                    <div className="flex flex-col items-center gap-2">
                        <Logo variante="completo" titulo="Tornado Digital" className="h-7 w-auto aspect-[1852/725] text-slate-400 dark:text-slate-600" />
                        <p className="text-slate-400 dark:text-slate-600 text-xs">© {new Date().getFullYear()} Tornado Digital</p>
                    </div>
                </div>
            </div>

            {/* Right Side: Visual Showcase */}
            <div className="hidden lg:flex flex-1 relative tornado-gradient items-center justify-center overflow-hidden">
                {/* Background Pattern/Effect */}
                <div
                    className="absolute inset-0 opacity-20"
                    style={{ backgroundImage: `url('data:image/svg+xml,%3Csvg width=\\'60\\' height=\\'60\\' viewBox=\\'0 0 60 60\\' xmlns=\\'http://www.w3.org/2000/svg\\'%3E%3Cg fill=\\'none\\' fill-rule=\\'evenodd\\'%3E%3Cg fill=\\'%23ffffff\\' fill-opacity=\\'1\\'%3E%3Cpath d=\\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')` }}
                ></div>

                {/* Content Container */}
                <div className="relative z-10 w-full max-w-lg px-8 flex flex-col items-center">

                    {/* Abstract Dashboard Mockup Placeholder */}
                    <div className="w-full aspect-[4/3] bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl p-4 mb-8 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                        <div className="w-full h-full bg-cover bg-center rounded-lg shadow-inner overflow-hidden relative group">
                            {/* Using a generic business dashboard image */}
                            <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-slate-900/0 transition-colors"></div>
                            <div className="w-full h-full bg-slate-800 flex flex-col">
                                {/* Fake UI Header */}
                                <div className="h-10 border-b border-white/10 flex items-center px-4 gap-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                </div>
                                {/* Fake UI Body with Chart */}
                                <div className="flex-1 p-6 flex items-center justify-center relative">
                                    <img
                                        alt="Data analytics dashboard showing sales growth charts"
                                        className="object-cover w-full h-full rounded opacity-90 hover:opacity-100 transition-opacity"
                                        src="https://lh3.googleusercontent.com/aida-public/AB6AXuD01bImVaifPCt0PRVfaaZjoMSCkzB2ZGoXUFfmeSzfoCBUB3Xf7Nr0j5CosKIzAUs87ELXzayzZykuSyPdSmnGKuQWcZ-e29WHb0t6zWtEvoNmIRaV5x9o-Okqn81RYfOjUTKFUhGW9t8L2ucAH-kMxvCgV-sqWsS8TLIsLx5_JyGZSHxld-Bs5k3B8NTJfPpS2-mOH0tdsAH5nDwd8w3v1CpF8_j6v4lMU7l6vdFsUydcz4212TzfkPyAq1edsT6xyqHxg4MntA"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-center">
                        <h3 className="text-3xl font-bold text-white mb-4 leading-tight">Impulsa tus ventas con el poder de TornadoCRM</h3>
                        <p className="text-white/80 text-lg font-light">
                            Gestiona leads, cierra tratos y automatiza tu flujo de trabajo en una sola plataforma unificada.
                        </p>
                    </div>

                    {/* Testimonial / Social Proof Pill */}
                    <div className="mt-10 flex items-center gap-3 bg-white/10 backdrop-blur-sm py-2 px-4 rounded-full border border-white/10">
                        <div className="flex -space-x-2">
                            <img alt="User avatar 1" className="w-8 h-8 rounded-full border-2 border-[#d63d00]" src="https://lh3.googleusercontent.com/aida-public/AB6AXuAgcWSkm6_Jud2ePGdjxyBjwWoY3Vfn6mfc344BfM_Gttnp5fWqKnNcZ0PBt3rB9vghwC-Ou3wFvp8oPnX3f5YQ9vkG77p_U126Ro2fyfT6AEiiHJRhqhhtkwOgzqjXHYr_y-RLZH2ziFU5thAzcPG2vx494ojhHbXIkYhsRMuxR7c9MpF_IJWKNwXLfnbeZ7bET3qDAebKxqasYKSrZkDrDWsqDpMGIkIwwAkisp9UaeK0psOzZEAKXziJFHfTVXbMwttcnhqsQw" />
                            <img alt="User avatar 2" className="w-8 h-8 rounded-full border-2 border-[#d63d00]" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDCjqgmxK28Sf_E-ZrBZQmnZZ5ObGX7sOIF8q58sC0TyifnePTrfSmQxVYUW41CPriWRNaZFmyVh26cYj7jFP2CbNU0UndAtrTFk2TzA8ewSLQgs9lQ0Ybb860izRkYYEvD7CTZdveMGcFw1KVBiSTR4W5KcBPUDAGT7VCVSWSqoXRrXSxK-IerOXjbugwtgJ6iAHJlt_0Q7invXxNwlhV_FDGdE5-eT1gDs_9l819sZcTMuaQkQt0ZoMG2_qv_AZmv9uIkAjbeNQ" />
                            <img alt="User avatar 3" className="w-8 h-8 rounded-full border-2 border-[#d63d00]" src="https://lh3.googleusercontent.com/aida-public/AB6AXuBbzQDNhnNFfT1hEDslrPLwFnoPDOPoKD94Lon7IBRl6tzkQFCN3D6WUxk0928qLX-lVJ_kAnq9SBga3BM7CACNyECaPwQNFiDh2Js-I5lrJltxRHjhxq8k36TGurOUEXg-5eyaCunmRBux2w37mqP5EYW7HxP1o_dJV-7HH6DtWoGOFkBhDuqE8DxIDxRu3kouZ0KRo_e63BmP7tnEQoKSXJDrQuGMLy3FT-IKeVYRNBKD5PPKDAHUzNWCKouWcAYQtgxg4a3HyA" />
                        </div>
                        <p className="text-white/90 text-sm font-medium">Usado por +10,000 equipos</p>
                    </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary rounded-full blur-[100px] opacity-50 pointer-events-none"></div>
                <div className="absolute top-0 left-0 w-64 h-64 bg-[#23150f] rounded-full blur-[100px] opacity-50 pointer-events-none"></div>
            </div>
        </div>
    );
}
